<template>
  <div @click="emit('onPlanSelected', product.id)" class="my-2 lg:my-0 transition duration-250 relative p-8 bg-white rounded-2xl shadow-sm flex flex-col lg:w-1/4 border-solid" :class="{ 'border-black border': isSelectedProduct, 'border border-gray-200': !isSelectedProduct, 'cursor-pointer': canSelectProduct }">
    <div class="flex-1 text-center">
      <h3 class="text-3xl font-gotham-bold text-brand-secondary">{{ product.name }}</h3>

      <div class="mt-0 lg:mt-4">
        <div class="flex justify-center text-center items-center text-gray-700">
          <div class="leading-7">
            <span :class="['tracking-tight font-gotham-bold', product.displayDiscount ? 'text-2xl text-gray-700 text-decoration-line: line-through' : 'text-5xl font-extrabold']">${{ product.price }}</span>
            <p v-if="product.displayDiscount" class="mb-1 tracking-tight font-extrabold text-4xl">${{ product.discountedPrice }}</p>
          </div>
          <span class="ml-1 text-xl font-gotham-bold">/Month</span>
        </div>
      </div>

      <div><span class="inline-flex items-center my-2 px-2.5 py-0.5 rounded-full text-md font-gotham-medium bg-brand-secondary text-white">Unlimited Data!</span></div>
      <p v-if="product.typicalEveningSpeed" class="my-1 lg:my-1 text-gray-700 font-gotham-bold"><span class="font-gotham-bold text-gray-700">{{ product.typicalEveningSpeed }} Mbps</span> <br>typical evening speed</p>
      <!-- Feature list -->
      <ul role="list" class="mt-2 mb-2 space-y-2 hidden lg:block font-gotham-medium">
        <li class="flex">
          <CheckIcon class="flex-shrink-0 w-8 h-8 text-brand-secondary-accent" aria-hidden="true" />
          <span class="ml-3 pt-1 text-gray-700 text-left">{{ product.description }}</span>
        </li>

        <li v-for="standardFeature in standardFeatures" :key="standardFeature" class="flex">
          <CheckIcon class="flex-shrink-0 w-8 h-8 text-brand-secondary-accent" aria-hidden="true" />
          <span class="ml-3 pt-1 text-gray-700">{{ standardFeature }}</span>
        </li>

        <li :key="product.name" class="flex text-left">
          <CheckIcon class="flex-shrink-0 w-8 h-8 text-brand-secondary-accent" aria-hidden="true" />
          <span class="ml-3 pt-1 text-gray-700">{{ product.download }} / {{ product.upload}}  (Mbps download/upload) </span>
        </li>
      </ul>
      <!-- Links -->
      <a v-for="standardLink in standardLinks" :key="standardLink.name" :href="standardLink.link" target="_blank" class="block href font-gotham-bold text-brand-primary hover:text-blue-800 hover:underline underline transition duration-250 my-1">
        {{ standardLink.name }}
      </a>
    </div>
    <button v-if="canSelectProduct" class="transition duration-250 text-gray-800" :class="[mostPopular, 'mt-0 lg:mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium text-gray-800', isSelectedProduct ? 'bg-brand-primary text-gray-800' : 'bg-brand-primary-light']">{{ isSelectedProduct ? 'Selected' : 'Select Plan'}}</button>
    <button v-else class="bg-brand-button font-gotham-bold text-brand-button-text px-8 py-2 rounded-full mx-6 lg:mx-10 mt-3" onclick="javascript:document.getElementById('primary-address-search').focus()">Check Address</button>
  </div>
</template>

<script setup lang='ts'>
import { CheckIcon } from '@heroicons/vue/outline';
import { defineProps, defineEmits, computed, ref } from 'vue';
import { BrandServiceInterface, PlanSelectorProductInformation } from '@/services/brand/brandServiceInterface';
import { injectStrict } from '@/plugins/vueServiceContainerPlugin';

const brandService = injectStrict<BrandServiceInterface>('BrandServiceInterface');

type Product = {
  id: string,
  name: string,
  description: string,
  download: number | null,
  upload: number | null,
  typicalEveningSpeed: number | null,
  price: number,
  discountedPrice?: number,
  discounted?: boolean,
  displayDiscount?: boolean
}

const props = defineProps<{
  canSelectProduct: boolean,
  isSelectedProduct: boolean,
  product: Product,
  planSelectorProductInformation: PlanSelectorProductInformation
}>();

const emit = defineEmits<{(event: 'onPlanSelected', id: string): void}>();

const mostPopular = computed(() => {
  return props.product.download === 250
    ? 'text-gray-800 hover:bg-brand-primary font-gotham-bold'
    : 'text-gray-800 hover:bg-brand-primary font-gotham-bold';
});

const standardFeatures = ref(props.planSelectorProductInformation.standardFeatures).value;
const standardLinks = ref(props.planSelectorProductInformation.standardLinks).value;

</script>
